import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'
import Toolbar from './dashboard-toolbar'
import DisplayView from '../display/display'
import EditForm from 'scoreboard/components/edit/edit-form'
import i18n from 'scoreboard/components/i18n'
import conductor from 'scoreboard/components/conductor'
import tabs from '../display/dashboards-tabs-template'
import formHelper from 'scoreboard/components/form-helper'
import FormView from 'scoreboard/components/form-view'
import mobile from 'scoreboard/components/mobile'
import layoutManager from 'scoreboard/components/layout-manager'
import constants from 'scoreboard/components/constants'
import addFirstHint from 'scoreboard/components/add-first-hint.hbs'
import license from 'scoreboard/components/license'
import dashboardControlsContainerTemplate from './dashboard-controls-container.hbs'
import menuManager from 'scoreboard/menu-manager'
import { isV5, isV6 } from 'scoreboard/components/version'
// Needed so that code inspector can know what file to open
// eslint-disable-next-line no-unused-vars
import DatasetTable from 'scoreboard/datasets/model/dataset-table'

import config from 'scoreboard/components/config'
import DashboardAiAssistant from './dashboard-ai-assistant'
import { triggerAddOrEdit } from 'scoreboard/components/domain-object-events'
import EditableCanvasContainerView from './editable-canvas-container-view'
import scrollNotifierTemplate from './scroll-notifier.hbs'

/**
 * Valid widget types for dashboards (as opposed to strategy maps)
 */
const DASHBOARD_WIDGET_TYPES = ['shape', 'charts', 'report', 'image', 'speedometer',
	'linear-gauge', 'notes', 'text', 'datasets', 'initiative-timeline', 'embedded-content',
	'initiative-chart',
	// These are all 'shape' widgets under the hood, but we give them different
	// names in the UI so they can be left out of strategy maps
	'shape', 'scorecard-bubble', 'initiative-bubble', 'line',
	// These are all speedometer and linear gauge widgets under the hood,
	// but we give them different names in the UI so they can be left out
	// of strategy maps
	'scorecard-speedometer', 'initiative-speedometer', 'dataset-speedometer',
	'scorecard-linear-gauge', 'initiative-linear-gauge', 'dataset-linear-gauge',
]

var EditView = DisplayView.extend({

	/** @type Dashboard */
	model: null,

	newModelNameCode: 'admin.performanceCharts.new',

	className: DisplayView.prototype.className + ' edit',
	subsection: 'edit',
	tabsTemplate: tabs,

	widgetTypes: DASHBOARD_WIDGET_TYPES,

	events: {
		'click .use-as-template': 'template'
	},

	initialize: function () {
		DisplayView.prototype.initialize.apply(this, arguments)

		this._setupToolbar()

		if (this.model.isNew()) {
			this.model.set('label', i18n.get(this.newModelNameCode))
		}

		this.editForm = new EditForm({
			section: 'dashboards',
			model: this.model,
			layoutOptions: this.options.layouts ? this._getLayouts() : null,
			layoutInstructions: this.options.layouts ? this._getLayoutInstructions() : null,
			labelInput: this.$('[name=label]')
		})

		// When the nav/org panes start to expand because of a long node name,
		// deselect the widgets in order to avoid any overlapping #27757
		menuManager.navTreeExpander.on('startExpand', () => this.canvas.deselectAll())
		menuManager.projectOrganizationMenu.on('isOverextended', () => this.canvas.deselectAll())
	},

	_getLayouts: function() {
		let options = [{
			value: 'blank',
			title: i18n.get('common.blank'),
			icon: 'layout-blank'
		},{
			value: 'speedometers',
			title: i18n.get('dashboards.dashboard.speedometers'),
			icon: 'speedometers'
		},{
			value: 'linear-gauges',
			title: i18n.get('dashboards.dashboard.linearGauges'),
			icon: 'linear-gauges'
		},{
			value: 'charts',
			title: i18n.get('charts.lineCharts'),
			icon: 'line-charts'
		},{
			value: 'bubbles',
			title: i18n.get('dashboards.dashboard.bubbles'),
			icon: 'performance-bubbles'
		}]
		// #54171 use "Enable AI Assistant" config option
		let aiAssistantEnabled = config.enableAiAssistant
		let aiAssistantOption = {
			value: 'aiAssistant',
			title: i18n.get('dashboards.dashboard.aiAssistant'),
			icon: 'ai-assistant'
		}

		return aiAssistantEnabled ? [aiAssistantOption, ...options] : options
	},

	_getLayoutInstructions: function() {
		// determine which banner message to display
		let orgData = this.options.orgData
		return layoutInstructions(orgData)
	},

	// Sets the initial enabled/disabled state of the layout options and selects
	// the appropriate starting option
	_setStartingOptions: function() {
		var options = this._getLayouts()

		// #17879 #53957 always default to the first option
		this.editForm.layoutOptions.val(options[0].value)

		// #16287 disable automatic layout options when requirements not met
		let hasMetrics = this.options.orgData.numMetrics && this.options.orgData.numMetrics != 0
		if (!hasMetrics) {
			// Disable 'Automatic' options
			options
				.filter(option => !_.includes(['blank', 'aiAssistant'], option.value))
				.forEach(option => this.editForm.layoutOptions.disable(option))
		}

		if (isV5()) {
			this.$('.dashboard-controls').addClass('is-showing-layout-options')
		}
		if (isV6()) {
			this.$el.addClass('is-showing-layout-options')
		}

	},

	_setupToolbar: function() {
		this.toolbar = new Toolbar({
			model: this.model,
			dirty: function() {
				return this.formView.dirty
			}.bind(this),
			save: function() {
				return this.submit(null,
					{ calendarPeriodId: this.getPeriodId() })
			}.bind(this),
			widgetTypes: secureWidgetTypes(this.widgetTypes),
			getPeriodId: () => this.getPeriodId()
		})

		this.toolbar.on('resize', () => this.resize())
		this.toolbar.on('swap', function() {
			// force re-rendering of the screen even though the url is unchanged
			Backbone.history.loadUrl(Backbone.history.fragment)
		})
		this.toolbar.on('save', () => this.formView.submit())
		this.toolbar.on('updateButtons', (mode, el) => {
			// If fullscreen mode, pass the edit-buttons and their attached
			// listeners/events to the to the toolbar.
			if (mode === 'fullscreen') {
				this.toolbar.fullscreenButtons(this.editForm.$el.find('.save-cancel'))
			} else if (mode === 'normalscreen') {
				// If leaving fullscreen mode, pass the edit-buttons and their attached
				// listeners/events back to the edit-form.
				this.editForm.$el.find('.edit-buttons').append(el)
			}
		})
	},

	_metaChange: function() {
		// potentially update the "New dashboard" placeholder in the tree
		this.trigger('change', formHelper.readFromDom(this.editForm.el))
	},

	_tabs: function() {
		// TODO: Remove when V5 is no longer supported
		// Only remove the isV6() check.
		if (isV6() && this.model.isNew()) return

		return this.tabsTemplate({
			id: this.model.id || this.model.get('relativeId'),
			isNew: this.model.isNew()
		})
	},

	render: function () {
		DisplayView.prototype.render.apply(this, arguments)

		if (isV5) {
			// If resetting the toolbar after canceling in fullscreen mode
			// #23570 Don't do it until after DisplayView has finished rendering
			// the canvas, because the toolbar.fullscreen() needs the canvas
			if (layoutManager.getLayout() == constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN) {
				this.toolbar.fullscreen()
			}
		}

		this.formView
			.on('change', this._metaChange.bind(this))
			// Listening for cancel event on the FormView instead of on the DOM node,
			// because FormView.cancel() disables the button, stopping the event bubbling
			.on('cancel', this.cancel.bind(this))

		this.$('.canvas').append(addFirstHint({hint: i18n.get('dashboards.addFirstWidget')}))

		this._secureControls()

		this.formView.start({
			isNew: this.model.isNew(),
			primaryButtonEl: this.$('.save')
		})

		// Fixes #59690, #17642
		// Append the auto-expand helper message
		this.$('.display-pane-body .canvas-container').append(`
			<div class="dashboard-auto-expand-wrapper">
				<span class="dashboard-auto-expand-message">
					${i18n.get('dashboards.autoExpandMessage')}
				</span>
			</div>
		`)

		let labelInput = this.$('[name=label]')
		labelInput.select()
		if (isV6()) {
			this.editForm.setUpMarkedField(labelInput)
		}

		return this
	},

	renderHeader: function() {
		DisplayView.prototype.renderHeader.apply(this, arguments)

		if (this.$('.dashboard-controls').length === 0) {
			this.$('.display-container').prepend(dashboardControlsContainerTemplate())
		}
		if (isV6()) {
			this.$('.display-container').append(this.editForm.el)
		}
		if (isV5()) {
			this.$('.dashboard-info').append(this.editForm.el)
		}
		this.editForm.render()
		this.$('.dashboard-toolbar').append(this.toolbar.el)
		this.toolbar.render()

		this.listenTo(this.header, 'change:period', this._changePeriod.bind(this))
		this.listenTo(this.editForm.layoutOptions, 'select', this._onChangeLayoutOption.bind(this))

		// Adjust which layout options are enabled/selected initially
		this.editForm.layoutOptions && this._setStartingOptions()
	},

	_secureControls: function () {
		// _updateState() in edit-buttons.js takes care of the move, edit, and copy buttons
		this.$('.dashboard-toolbar, .canvas, .dashboard-scroll-notifier').toggleClass('is-not-displayed', this.model.isNew())

		// Can't change label for dashboards that are template copies in an organization-level template
		// See edit-form.js and template-naming-panel-contents.js
		if (this.model.get('templateId') && this.model.get('organizationIsCopy')) {
			this.$('.edit-form-label').addClass('is-not-displayed')
			this.$('.dashboard-data .table-hack-cell').prepend('<div class="banner cannot-edit-message">' + this.cannotEditTemplateMessage() + '</div>')
		}

		// If this is a direct template, insert a banner explaining that
		if (this.model.get('templateId') && !this.model.get('organizationIsCopy')) {
			this.$('.dashboard-data .table-hack-cell').prepend('<div class="banner cannot-edit-message">' + this.cannotEditDirectTemplateMessage() +'</div>')
		}
	},

	cannotEditTemplateMessage: function() {
		return i18n.get('dashboards.cannotEditDashboard')
	},

	cannotEditDirectTemplateMessage: function() {
		return i18n.get('dashboards.cannotEditDashboardSuffixPrefix')
	},

	createCanvasContainer(el) {
		this.createFormView()

		// Append the triangle in the bottom right corner of the canvas area
		let scrollNotifier = $(scrollNotifierTemplate())
		this.$('.display-pane-body').append(scrollNotifier)

		let canvasContainer = new EditableCanvasContainerView({
			el,
			model: this.model,
			wobs: this.wobs,
			maestro: this.maestro,
			toolbar: this.toolbar,
			formView: this.formView,
			scrollNotifier,
			minHeight: DisplayView.CANVAS_MIN_HEIGHT,
			minWidth: DisplayView.CANVAS_MIN_WIDTH
		})

		this.listenTo(canvasContainer, 'resize', () => {
			// When canvasContainer fires its resize event, it will take care
			// of its own resizing, so we only need to do the non-canvas stuff
			this.resizeNonCanvas()
		})

		return canvasContainer
	},

	createFormView () {
		this.formView = new FormView({
			toggleCancel: true,
			cancelOnEsc: () => this.model.isNew(),
			// Use the .dashboard-controls element, which contains the <form>
			// element and the buttons used in fullscreen edit mode; if we use
			// this.el, then the FormView logic will get confused by the buttons
			// that show up in note widgets
			// TODO: Remove when V5 is no longer supported.
			// Remove the non v6 portion only
			el: isV6() ? this.$el : this.$('.dashboard-controls'),
			submit: this.submit.bind(this),
			allowCleanSubmission: () => {
				// #43166 the name is pre-populated, so allow a clean save for
				// new dashboards.
				// #44355 but if it's not new, wait for changes before allowing save
				return this.model.isNew()
			}
		})
	},

	cancel: function() {
		if (this.model.isNew()) {
			if (conductor.id()) {
				conductor.edit()
			} else {
				var url = 'organizations/' + this.model.get('organizationId') + '/dashboards'
				conductor.navigate(url, { trigger: true })
			}
		} else {
			this.toolbar.trigger('canceled')
			// Even though we could keep the old model around, we'd still need fresh data
			// from the server in case the period was changed and nodes have been removed.
			// So for now, just do what's easy and reload the whole thing
			conductor.reload()
		}
	},

	submit: function (e, options) {
		let label = this.$('[name=label]').val()
		if (this.$('.layout-options').is(':visible') &&
			this.editForm.layoutOptions?.val() == 'aiAssistant') {
			this.dashboardAiAssistant = new DashboardAiAssistant({
				dashboardLabel: label
			})
			this.$('.dashboard-info').html(this.dashboardAiAssistant.el)
			this.dashboardAiAssistant.render()
			return
		}

		const data = {
			...this.canvasContainer.getSaveData(),
			label
		}

		var isNew = this.model.isNew()

		// the swap functionality requires the new data be returned to work
		// properly, so this is a hook to allow that data to be returned in the
		// response when the dashboard is saved prior to opening the swap dialog
		var url = _.result(this.model, 'url')

		let params = {}

		if (isNew && this.editForm.layoutOptions) {
			params.layout = this.editForm.layoutOptions.val()
		}

		var periodId = _.get(options, 'calendarPeriodId')
		if (!periodId && isNew && params.layout !== 'blank') {
			// If this is a new dashboard, pass along the period so the autogenerated widgets will include
			// current data
			periodId = this.getPeriodId()
		}

		if (periodId) {
			params.calendarPeriodId = periodId
		}

		url += '?' + $.param(params)

		// Since the wobs can be repositioned upon dashboard save, make sure
		// the expand message is visible if it should be
		this.canvasContainer.toggleExpandMessage()

		return this.model.save(data, {
			url: url,
			patch: true,
			success: function (model, response) {
				this.trigger('save', model)
				var action = isNew ? 'add' : 'edit'
				triggerAddOrEdit(action, model)

				// Remove the layout options
				this.editForm.removeLayoutOptions()

				if (periodId) {
					this.model.data.set(response.data)
				}

				const responseWobIds = response.widgets.map(it => it.id)

				// For any new wobs, or wobs that have been saved under a different ID
				// from the one that was sent, we need to get the corresponding ID that
				// was returned from the server. It returns widgets in the same order
				// they were sent, so we can just do it by index.
				this.wobs.chain().forEach(function (wob, i) {
					var id = responseWobIds[i]
					wob.set('id', id)
					wob.widget.set('id', id)
				}).value()

				// New images are sent to the server as a dataUri, saved to the database, and given
				// an imageId. We need to clear the dataUri off of the wob and set the imageId property
				_(response.widgets).filter(function (node) {
						return node.properties && node.properties.imageId
					})
					.forEach(function (node) {
						var widget = this.wobs.get(node.id).widget
						widget.unset('image')
						widget.properties.set('imageId', node.properties.imageId)
					}.bind(this))

				// Match up new chart IDs with the corresponding widget
				_(response.widgets).filter(function (node) {
						return node.properties && node.properties.chartId
					})
					.forEach(function (node) {
						let wob = this.wobs.get(node.id)
						if (!wob) {
							// this can occur if we just autogenerated charts on a new dashboard;
							// no need to remap things, the chart IDs will be populated already
							return
						}

						var widget = wob.widget

						if (widget.properties.get('type') === 'chart-table') {
							if (widget.properties.get('chartId') != node.properties.chartId) {
								widget.properties.set({
									chartId: node.properties.chartId,
									chartTempId: null
								})
							}
						} else if (widget.properties.get('type') === 'chart') {
							if (widget.properties.get('chartId') != node.properties.chartId) {
								// #43378 Must be set before properties update, (below), to
								// ensure that the resulting fetch has the new id.
								widget.chart.set({
									id: node.properties.chartId,
									chartTempId: null
								}, {
									silent: true
								})
								widget.properties.set({
									chartId: node.properties.chartId,
									chartTempId: null
								})
							}
						}
					}.bind(this))

				_(response.widgets)
					.filter(node => !!node.thresholdColoring)
					.forEach(function (node) {
						let wob = this.wobs.get(node.id)

						wob.get('thresholdColoring').id = node.thresholdColoring.id
					}.bind(this))

				// Match up new report subtype IDs with the corresponding widget
				let validSubtypeAndIds = []
				_(response.widgets).filter(function (node) {
					return node.properties && node.properties.reportSubtypeId
				})
					.forEach(function (node) {
						var widget = this.wobs.get(node.id).widget

						let subtypeAndId = {
							reportSubtype: node.properties.reportSubtype,
							reportSubtypeId: node.properties.reportSubtypeId
						}

						validSubtypeAndIds.push(subtypeAndId)
						widget.properties.set(subtypeAndId)
					}.bind(this))

				this.removeDeletedReportsFromHistory(validSubtypeAndIds)

				// Update the connectors ids
				_(response.widgets).filter(function (node) {
						return !_.isEmpty(node.connectors)
					})
					.forEach(function (node) {
						this.wobs.get(node.id)
							.get('connectors')
							.forEach(function (connector, i) {
								connector.set('id', node.connectors[i])
							})
					}.bind(this))


				// Match up new table IDs with the corresponding widget
				_(response.widgets).filter((node) => node.table)
					.forEach((node) => {
						const widget = this.wobs.get(node.id).widget
						widget.table = new DatasetTable(node.table)
				})

				if (isNew) {
					// #9939 #11186 show correct header buttons and enable the
					// overview link
					this.renderHeader()
					this._secureControls()

					// takes care of any height adjustments that were done to
					// fit in the layoutOptions (see resize() method for details)
					this.resize()

					if (this.model.wobData().length > 0) {
						// The server auto-generated some widgets; add them to the canvas
						this.model.wobData().forEach((w) => this.canvas.create(w, null, { select: false }))

						// Since the user is seeing this as a brand new dashboard, clear out any undo state that
						// was created while adding the autogenerated widgets
						this.canvas.history.state.clear()
						this.toolbar.toggleUndo(false)

						// #61418 also need to fire a maestro query, since chart
						// data is fetched after the widgets are rendered
						this.maestro.queryAll()
					}
				}

				this.formView.start({
					isNew: false,
					primaryButtonEl: this.$('.save')
				})

			}.bind(this)
		})
	},

	/**
	 * Cleans up canvas's history to clear out any report IDs that have been
	 * deleted from the database.
	 */
	removeDeletedReportsFromHistory (validSubtypeAndIds) {
		this.canvas.history.state.undos.concat(this.canvas.history.state.redos).forEach(action => {
			let state = JSON.parse(action.state)

			// Looks at each wob in this undo/redo action's state
			state.forEach(wob => {
				let props = _.get(wob, 'widget.properties')
				if (props && props.type === 'report') {
					// See if the undo/redo wob is in the list of report IDs
					// that still exist on the server
					let stillValid = !!_.find(validSubtypeAndIds, valid => {
						return valid.reportSubtype == props.reportSubtype
							&& valid.reportSubtypeId == props.reportSubtypeId
					})

					// If not, the wob's report has been deleted. Clear out the
					// IDs so that it will be treated as a new unsaved report
					if (!stillValid) {
						delete props.reportSubtype
						delete props.reportSubtypeId
					}
				}
			})

			action.state = JSON.stringify(state)
		})
	},

	_changePeriod: function (periodId) {
		if (this.model.isNew()) {
			return
		}

		this.model.data.fetch({ calendarPeriodId: periodId })
	},

	_onChangeLayoutOption: function(option) {
		let buttonKey = option.value == 'aiAssistant' ? 'common.next' : 'common.create'
		this.$('.button-primary').text(i18n.get(buttonKey))
	},

	/**
	 * Performs all resize logic, including the canvas
	 *
	 * @param boundingBox the new bounding box that the canvas should fit within
	 */
	resize: function (boundingBox) {
		this.resizeNonCanvas()
		this.canvasContainer.resize(boundingBox)
	},

	/**
	 * Performs resize logic not related to the canvas itself
	 */
	resizeNonCanvas () {
		var displayPane = $('.display-pane')

		// If the .box-options become more narrow than 705px, change their layout to fit.
		// This width is somewhere between still within the realm of .display-large but the options
		// need to adjust - which is why we're appending this extra class.
		this.$('.box-options').toggleClass('narrow-dashboard-options', this.$el.width() < 705)

		let layoutOptionsVisible = this.model.isNew() &&
			this.editForm.layoutOptions && !mobile.isMobileWidth()
		if (isV5()) {
			if (layoutOptionsVisible) {
				// When the layout options are visible, we need to manually size their container
				// so that they do not end up appearing out of view without scrollbars #15967
				// We don't want this after the dashboard has been saved (ie !this.model.isNew())
				this.$el.find('.dashboard-data .dashboard-info').css('height', $(window).height() - this.$('header').height() - 2)
			}
		}
		if (!layoutOptionsVisible) {
			if (isV5()) {
				this.$('.dashboard-controls').removeClass('is-showing-layout-options')
			}
			if (isV6()) {
				this.$el.removeClass('is-showing-layout-options')
			}
			this.$('.display-pane-body').removeClass('is-not-displayed')
			this.$el.find('.dashboard-data .dashboard-info').css('height', '')
		}

		// -webkit-overflow-scrolling:auto causes the .display-pane to not scroll properly
		// in the strategy map and dashboards screens (#16298). This is due to a problem
		// with the style property in mobile safari which can be solved by simply resetting it in the js.
		// https://stackoverflow.com/questions/26738764/ios8-safari-webkit-overflow-scrolling-touch-issue#26743685
		// This is located here instead of main.js because the problem only occurs here
		// and this fix needs to run each time the dashboard/strategy map is saved
		setTimeout(() => {
			displayPane.css('-webkit-overflow-scrolling','touch')
		})
		displayPane.css('-webkit-overflow-scrolling','auto')

		// Update the toolbar with how much canvas-area space we have so it can use it for it's menu
		_.invoke(this.toolbar, 'setMenuSpace', {
			width: this.canvasContainer.$el.width(),
			height: this.el.clientHeight
		})
	},

	remove: function () {
		DisplayView.prototype.remove.apply(this, arguments)
		_.invokeMap([this.toolbar, this.editForm, this.formView,
			this.dashboardAiAssistant], 'remove')
	}

})

/**
 * Returns the layout instructions for the given organization data
 * and if the Ai Assistant is enabled.
 * #54483
 */
const layoutInstructions = (orgData) =>{
	const { numMetrics, label } = orgData
	const escapedLabel = _.escape(label)
	const hasMetrics = numMetrics && numMetrics !== 0
	// Determine the AI assistant suffix based on configuration.
	const aiAssistantSuffix = config.enableAiAssistant ? '.aiAssistant' : ''
	// Construct the base key based on whether metrics are available.
	const baseKey = `dashboards.layout.${hasMetrics ? 'new' : 'noMetrics'}.instructions`
	const key = baseKey + aiAssistantSuffix
	// if there are metrics, the key needs the number of metrics and label as args, for no metrics, just the label
	return hasMetrics ? i18n.get(key, numMetrics, escapedLabel) : i18n.get(key, escapedLabel)
}

/**
 * From the given array of widget types, only returns the ones that should be
 * displayed based on the current license.
 * @param {String[]} widgetTypes
 * @return {String[]}
 */
function secureWidgetTypes (widgetTypes) {
	// #16346 do not show menu items for sections that are not enabled
	// #21180 hide "shared image" ui if documents are not enabled
	if (!license.files) {
		_.pull(widgetTypes, 'image')
	}
	// note: with #48947, license.forms is based on license.datasets
	return _.filter(widgetTypes, (type) => license[type] != false)
}

export default EditView
export { layoutInstructions, secureWidgetTypes, DASHBOARD_WIDGET_TYPES }

EditView.__filename__ = 'dashboards/edit/dashboards-edit.js'
